import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { formatCurrency, parseCurrency } from '../utils/formatters';
import AddOutlined from '@mui/icons-material/AddOutlined';
import PersonRemoveOutlined from '@mui/icons-material/PersonRemoveOutlined';
import PersonAddOutlined from '@mui/icons-material/PersonAddOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';
import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined';
import API_BASE from '../config/api';

const TABS = ['Overview', 'Tasks & Notes', 'Events', 'Team', 'Activity'];
const STATUS_OPTIONS = ['Proposal', 'Active', 'On Hold', 'Completed', 'Archived'];

const STATUS_COLORS = {
  'Proposal':  { bg: 'rgba(229,192,75,0.1)',  color: '#E5C04B' },
  'Active':    { bg: 'rgba(57,255,20,0.1)',   color: '#39FF14' },
  'On Hold':   { bg: 'rgba(229,192,75,0.1)',  color: '#E5C04B' },
  'Completed': { bg: 'rgba(83,145,255,0.1)',  color: '#5391FF' },
  'Archived':  { bg: 'rgba(86,86,86,0.2)',    color: '#565656' },
};

function toDateInput(d) {
  if (!d) return '';
  const date = new Date(d);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

function initFormData(doc) {
  return {
    title: doc.title || '',
    status: doc.status || 'Proposal',
    start_date: toDateInput(doc.start_date),
    end_date: toDateInput(doc.end_date),
    target_budget: doc.target_budget != null ? String(doc.target_budget) : '',
    project_city: doc.project_city || '',
    project_state: doc.project_state || '',
    notes: doc.notes || '',
  };
}

function TaskCheckbox({ checked, onChange }) {
  return (
    <div
      onClick={onChange}
      style={{
        width: '18px',
        height: '18px',
        minWidth: '18px',
        borderRadius: '3px',
        border: checked ? 'none' : '2px solid #565656',
        backgroundColor: checked ? '#39FF14' : 'transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.15s ease',
      }}
    >
      {checked && (
        <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
          <path
            d="M1 4L4 7L10 1"
            stroke="#1a1a1a"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
}

export default function ProjectDetail({ project, onUpdate }) {
  const { token } = useAuth();
  const { activeWorkspace } = useWorkspace();

  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState(() => initFormData(project));
  const formDataRef = useRef(formData);
  const [localProject, setLocalProject] = useState(project);
  const [savedFields, setSavedFields] = useState({});
  const [errorFields, setErrorFields] = useState({});
  const [newTaskText, setNewTaskText] = useState('');
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [archiveHover, setArchiveHover] = useState(false);
  const [budgetDisplay, setBudgetDisplay] = useState(() => formatCurrency(project.target_budget) || '');

  // Client state
  const [linkedClient, setLinkedClient] = useState(null);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [availableClients, setAvailableClients] = useState([]);
  const clientPickerRef = useRef(null);

  // Team state
  const [projectTeam, setProjectTeam] = useState([]);
  const [showMemberPicker, setShowMemberPicker] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [availablePersonnel, setAvailablePersonnel] = useState([]);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);
  const [memberPositions, setMemberPositions] = useState({});
  const memberPickerRef = useRef(null);

  // PM state
  const [linkedPM, setLinkedPM] = useState(null);
  const [showPMPicker, setShowPMPicker] = useState(false);
  const [pmSearch, setPmSearch] = useState('');
  const pmPickerRef = useRef(null);

  // Task assignee state
  const [activeTaskPicker, setActiveTaskPicker] = useState(null);
  const [taskSearch, setTaskSearch] = useState('');
  const [taskAssignees, setTaskAssignees] = useState({});
  const taskPickerRef = useRef(null);

  // Events state
  const [projectEvents, setProjectEvents] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [addEventHover, setAddEventHover] = useState(false);
  const [hoveredEventId, setHoveredEventId] = useState(null);

  const navigate = useNavigate();

  // Sync formDataRef
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Reset all state when project changes; fetch client + team profiles
  useEffect(() => {
    setFormData(initFormData(project));
    setBudgetDisplay(formatCurrency(project.target_budget) || '');
    setLocalProject(project);
    setActiveTab(0);
    setSavedFields({});
    setErrorFields({});
    setNewTaskText('');
    setLinkedClient(null);
    setShowClientPicker(false);
    setClientSearch('');
    setProjectTeam([]);
    setShowMemberPicker(false);
    setMemberSearch('');
    setLinkedPM(null);
    setShowPMPicker(false);
    setPmSearch('');
    setActiveTaskPicker(null);
    setTaskSearch('');
    setTaskAssignees({});
    setProjectEvents([]);
    setIsLoadingEvents(true);

    // Fetch events for this project (workspace_id set below after workspace loads)
    // Actual fetch is in separate effect keyed on project._id + activeWorkspace

    // Init member positions from project.team_members
    const posMap = {};
    (project.team_members || []).forEach((m) => {
      if (m.personnel_profile_id) {
        posMap[m.personnel_profile_id.toString()] = m.position || '';
      }
    });
    setMemberPositions(posMap);

    // Fetch linked client
    if (project.client_id) {
      axios
        .get(`${API_BASE}/api/companies/${project.client_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => setLinkedClient(res.data))
        .catch(() => {});
    }

    // Fetch team member profiles
    if (project.team_members && project.team_members.length > 0) {
      setIsLoadingTeam(true);
      Promise.all(
        project.team_members.map((m) =>
          axios
            .get(`${API_BASE}/api/personnel/${m.personnel_profile_id}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => res.data)
            .catch(() => null)
        )
      ).then((profiles) => {
        const loaded = profiles.filter(Boolean);
        setProjectTeam(loaded);
        setIsLoadingTeam(false);
        // Pre-populate taskAssignees for any tasks that already have an assignee
        const assigneeMap = {};
        (project.tasks || []).forEach((task) => {
          if (task.assignee_id) {
            const profile = loaded.find(
              (p) => p._id?.toString() === task.assignee_id?.toString()
            );
            if (profile) assigneeMap[task._id] = profile;
          }
        });
        setTaskAssignees(assigneeMap);
      });
    }
  }, [project._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch available clients and personnel
  useEffect(() => {
    if (activeWorkspace) {
      axios
        .get(`${API_BASE}/api/companies`, {
          params: { workspace_id: activeWorkspace._id, type: 'client' },
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => setAvailableClients(res.data))
        .catch(() => {});
    }
    axios
      .get(`${API_BASE}/api/personnel`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setAvailablePersonnel(res.data))
      .catch(() => {});
  }, [activeWorkspace?._id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch events for this project
  useEffect(() => {
    if (!activeWorkspace) return;
    setIsLoadingEvents(true);
    setProjectEvents([]);
    axios
      .get(`${API_BASE}/api/events`, {
        params: { workspace_id: activeWorkspace._id, project_id: localProject._id },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setProjectEvents(res.data);
        setIsLoadingEvents(false);
      })
      .catch(() => setIsLoadingEvents(false));
  }, [localProject._id, activeWorkspace?._id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve PM from availablePersonnel when project or personnel changes
  useEffect(() => {
    if (localProject.project_manager_id && availablePersonnel.length > 0) {
      const found = availablePersonnel.find(
        (p) => p._id?.toString() === localProject.project_manager_id?.toString()
      );
      setLinkedPM(found || null);
    } else if (!localProject.project_manager_id) {
      setLinkedPM(null);
    }
  }, [localProject.project_manager_id, availablePersonnel.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Outside click handler for pickers
  useEffect(() => {
    function handleClickOutside(e) {
      if (clientPickerRef.current && !clientPickerRef.current.contains(e.target)) {
        setShowClientPicker(false);
      }
      if (memberPickerRef.current && !memberPickerRef.current.contains(e.target)) {
        setShowMemberPicker(false);
      }
      if (pmPickerRef.current && !pmPickerRef.current.contains(e.target)) {
        setShowPMPicker(false);
      }
      if (taskPickerRef.current && !taskPickerRef.current.contains(e.target)) {
        setActiveTaskPicker(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const headers = { headers: { Authorization: `Bearer ${token}` } };

  function showSaved(field) {
    setSavedFields((prev) => ({ ...prev, [field]: true }));
    setTimeout(() => setSavedFields((prev) => ({ ...prev, [field]: false })), 2000);
  }

  function showError(field) {
    setErrorFields((prev) => ({ ...prev, [field]: true }));
    setTimeout(() => setErrorFields((prev) => ({ ...prev, [field]: false })), 3000);
  }

  async function handleBlur(field, precomputedValue) {
    const value = precomputedValue !== undefined ? precomputedValue : formDataRef.current[field];
    let payload;
    if (field === 'start_date' || field === 'end_date') {
      payload = { [field]: value || null };
    } else if (field === 'target_budget') {
      payload = { target_budget: value === '' || value === null ? null : Number(value) };
    } else {
      payload = { [field]: value };
    }
    try {
      const res = await axios.put(
        `${API_BASE}/api/projects/${localProject._id}`,
        payload,
        headers
      );
      setLocalProject(res.data);
      onUpdate(res.data);
      showSaved(field);
    } catch {
      showError(field);
    }
  }

  async function handleStatusChange(newStatus) {
    setFormData((prev) => ({ ...prev, status: newStatus }));
    try {
      const res = await axios.put(
        `${API_BASE}/api/projects/${localProject._id}`,
        { status: newStatus },
        headers
      );
      setLocalProject(res.data);
      onUpdate(res.data);
      showSaved('status');
    } catch {
      showError('status');
    }
  }

  async function handleToggleTask(task) {
    const newComplete = !task.is_complete;
    setLocalProject((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t) =>
        t._id === task._id ? { ...t, is_complete: newComplete } : t
      ),
    }));
    try {
      const res = await axios.put(
        `${API_BASE}/api/projects/${localProject._id}/tasks/${task._id}`,
        { is_complete: newComplete },
        headers
      );
      setLocalProject(res.data);
      onUpdate(res.data);
    } catch {
      setLocalProject((prev) => ({
        ...prev,
        tasks: prev.tasks.map((t) =>
          t._id === task._id ? { ...t, is_complete: task.is_complete } : t
        ),
      }));
    }
  }

  async function handleAddTask() {
    const text = newTaskText.trim();
    if (!text) return;
    setIsAddingTask(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/projects/${localProject._id}/tasks`,
        { text },
        headers
      );
      setLocalProject(res.data);
      onUpdate(res.data);
      setNewTaskText('');
    } catch {
      // silent
    } finally {
      setIsAddingTask(false);
    }
  }

  async function handleDeleteTask(taskId) {
    setLocalProject((prev) => ({
      ...prev,
      tasks: prev.tasks.filter((t) => t._id !== taskId),
    }));
    try {
      const res = await axios.delete(
        `${API_BASE}/api/projects/${localProject._id}/tasks/${taskId}`,
        headers
      );
      setLocalProject(res.data);
      onUpdate(res.data);
    } catch {
      // silent
    }
  }

  async function handleArchive() {
    try {
      const res = await axios.put(
        `${API_BASE}/api/projects/${localProject._id}`,
        { is_archived: true, status: 'Archived' },
        headers
      );
      setLocalProject(res.data);
      setFormData((prev) => ({ ...prev, status: 'Archived' }));
      onUpdate(res.data);
    } catch {
      // silent
    }
  }

  // Client handlers
  async function handleSelectClient(company) {
    try {
      const res = await axios.put(
        `${API_BASE}/api/projects/${localProject._id}`,
        { client_id: company._id },
        headers
      );
      setLocalProject(res.data);
      onUpdate(res.data);
      setLinkedClient(company);
      setShowClientPicker(false);
      setClientSearch('');
    } catch {
      // silent
    }
  }

  async function handleRemoveClient() {
    try {
      const res = await axios.put(
        `${API_BASE}/api/projects/${localProject._id}`,
        { client_id: null },
        headers
      );
      setLocalProject(res.data);
      onUpdate(res.data);
      setLinkedClient(null);
    } catch {
      // silent
    }
  }

  // PM handlers
  async function handleSelectPM(profile) {
    try {
      // 1. Save project_manager_id
      const pmRes = await axios.put(
        `${API_BASE}/api/projects/${localProject._id}`,
        { project_manager_id: profile._id },
        headers
      );
      setLocalProject(pmRes.data);
      onUpdate(pmRes.data);
      setLinkedPM(profile);
      setShowPMPicker(false);
      setPmSearch('');

      // 2. Add/update on team
      const profileIdStr = profile._id?.toString();
      const alreadyOnTeam = projectTeam.some(
        (p) => p._id?.toString() === profileIdStr
      );

      if (alreadyOnTeam) {
        // Update existing position to "Project Manager"
        const updatedMembers = (pmRes.data.team_members || []).map((m) =>
          m.personnel_profile_id?.toString() === profileIdStr
            ? { ...m, position: 'Project Manager' }
            : m
        );
        const teamRes = await axios.put(
          `${API_BASE}/api/projects/${localProject._id}`,
          { team_members: updatedMembers },
          headers
        );
        setLocalProject(teamRes.data);
        onUpdate(teamRes.data);
        setMemberPositions((prev) => ({ ...prev, [profileIdStr]: 'Project Manager' }));
      } else {
        // Add to team
        const newMembers = [
          ...(pmRes.data.team_members || []),
          { personnel_profile_id: profile._id, position: 'Project Manager', added_at: new Date() },
        ];
        const teamRes = await axios.put(
          `${API_BASE}/api/projects/${localProject._id}`,
          { team_members: newMembers },
          headers
        );
        setLocalProject(teamRes.data);
        onUpdate(teamRes.data);
        setProjectTeam((prev) => [...prev, profile]);
        setMemberPositions((prev) => ({ ...prev, [profileIdStr]: 'Project Manager' }));
      }
    } catch {
      // silent
    }
  }

  async function handleRemovePM() {
    try {
      const res = await axios.put(
        `${API_BASE}/api/projects/${localProject._id}`,
        { project_manager_id: null },
        headers
      );
      setLocalProject(res.data);
      onUpdate(res.data);

      // Clear position for former PM (keep on team, just blank the position)
      if (linkedPM) {
        const profileIdStr = linkedPM._id?.toString();
        const updatedMembers = (res.data.team_members || []).map((m) =>
          m.personnel_profile_id?.toString() === profileIdStr
            ? { ...m, position: '' }
            : m
        );
        const teamRes = await axios.put(
          `${API_BASE}/api/projects/${localProject._id}`,
          { team_members: updatedMembers },
          headers
        );
        setLocalProject(teamRes.data);
        onUpdate(teamRes.data);
        setMemberPositions((prev) => ({ ...prev, [profileIdStr]: '' }));
      }

      setLinkedPM(null);
    } catch {
      // silent
    }
  }

  // Task assignee handler — profile is a full profile object or null to unassign
  async function handleAssignTask(taskId, profile) {
    setActiveTaskPicker(null);
    setTaskSearch('');
    if (profile) {
      setTaskAssignees((prev) => ({ ...prev, [taskId]: profile }));
    } else {
      setTaskAssignees((prev) => {
        const next = { ...prev };
        delete next[taskId];
        return next;
      });
    }
    try {
      const res = await axios.put(
        `${API_BASE}/api/projects/${localProject._id}/tasks/${taskId}`,
        { assignee_id: profile ? profile._id : null },
        headers
      );
      setLocalProject(res.data);
      onUpdate(res.data);
    } catch {
      // silent
    }
  }

  // Team handlers
  async function handleAddMember(profile) {
    const newEntry = {
      personnel_profile_id: profile._id,
      position: '',
      added_at: new Date(),
    };
    const updatedMembers = [...(localProject.team_members || []), newEntry];
    try {
      const res = await axios.put(
        `${API_BASE}/api/projects/${localProject._id}`,
        { team_members: updatedMembers },
        headers
      );
      setLocalProject(res.data);
      onUpdate(res.data);
      setProjectTeam((prev) => [...prev, profile]);
      setMemberPositions((prev) => ({ ...prev, [profile._id.toString()]: '' }));
      setShowMemberPicker(false);
      setMemberSearch('');
    } catch {
      // silent
    }
  }

  async function handleRemoveMember(profileId) {
    const updatedMembers = (localProject.team_members || []).filter(
      (m) => m.personnel_profile_id?.toString() !== profileId
    );
    try {
      const res = await axios.put(
        `${API_BASE}/api/projects/${localProject._id}`,
        { team_members: updatedMembers },
        headers
      );
      setLocalProject(res.data);
      onUpdate(res.data);
      setProjectTeam((prev) => prev.filter((p) => p._id?.toString() !== profileId));
      setMemberPositions((prev) => {
        const next = { ...prev };
        delete next[profileId];
        return next;
      });
    } catch {
      // silent
    }
  }

  async function handleUpdatePosition(profileId, position) {
    const updatedMembers = (localProject.team_members || []).map((m) =>
      m.personnel_profile_id?.toString() === profileId ? { ...m, position } : m
    );
    try {
      const res = await axios.put(
        `${API_BASE}/api/projects/${localProject._id}`,
        { team_members: updatedMembers },
        headers
      );
      setLocalProject(res.data);
      onUpdate(res.data);
    } catch {
      // silent
    }
  }

  // Style constants
  const LABEL_STYLE = {
    fontFamily: "'Roboto', sans-serif",
    fontWeight: 500,
    fontSize: '11px',
    color: '#888888',
    letterSpacing: '1px',
    marginBottom: '6px',
    display: 'block',
  };

  const INPUT_STYLE = {
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid #565656',
    color: '#F5F5F5',
    fontFamily: "'Roboto', sans-serif",
    fontSize: '14px',
    padding: '4px 0',
    outline: 'none',
    width: '100%',
  };

  const SECTION_HEADER_STYLE = {
    fontFamily: "'Roboto', sans-serif",
    fontWeight: 500,
    fontSize: '11px',
    color: '#565656',
    letterSpacing: '2px',
    paddingBottom: '8px',
    borderBottom: '1px solid #333333',
    marginBottom: '20px',
    marginTop: '28px',
  };

  const SAVED_STYLE = {
    fontFamily: "'Roboto', sans-serif",
    fontSize: '11px',
    color: '#39FF14',
    marginTop: '3px',
  };

  const ERROR_STYLE = {
    fontFamily: "'Roboto', sans-serif",
    fontSize: '11px',
    color: '#BF2634',
    marginTop: '3px',
  };

  const PICKER_DROPDOWN_STYLE = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#2a2a2a',
    border: '1px solid #3a3a3a',
    borderRadius: '6px',
    maxHeight: '240px',
    overflowY: 'auto',
    zIndex: 100,
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
  };

  const PICKER_SEARCH_STYLE = {
    width: '100%',
    padding: '8px 12px',
    background: '#1a1a1a',
    border: 'none',
    borderBottom: '1px solid #3a3a3a',
    color: '#F5F5F5',
    fontFamily: "'Roboto', sans-serif",
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  };

  // ─── TAB RENDERERS ────────────────────────────────────────────────

  function renderOverview() {
    const sc = STATUS_COLORS[formData.status] || STATUS_COLORS['Active'];
    const filteredClients = availableClients.filter((c) =>
      c.name.toLowerCase().includes(clientSearch.toLowerCase())
    );

    const filteredPM = availablePersonnel.filter((p) =>
      `${p.first_name} ${p.last_name}`.toLowerCase().includes(pmSearch.toLowerCase())
    );

    return (
      <div>
        {/* IDENTITY */}
        <div style={{ ...SECTION_HEADER_STYLE, marginTop: '4px' }}>IDENTITY</div>

        <div style={{ marginBottom: '20px' }}>
          <label style={LABEL_STYLE}>TITLE</label>
          <input
            value={formData.title}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            onBlur={() => handleBlur('title')}
            style={INPUT_STYLE}
          />
          {savedFields.title && <div style={SAVED_STYLE}>Saved</div>}
          {errorFields.title && <div style={ERROR_STYLE}>Error saving</div>}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={LABEL_STYLE}>STATUS</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <select
              value={formData.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              style={{
                ...INPUT_STYLE,
                width: 'auto',
                cursor: 'pointer',
                backgroundColor: sc.bg,
                color: sc.color,
                border: `1px solid ${sc.color}`,
                borderRadius: '10px',
                padding: '3px 10px',
                fontWeight: 500,
                fontSize: '12px',
              }}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {savedFields.status && <span style={SAVED_STYLE}>Saved</span>}
            {errorFields.status && <span style={ERROR_STYLE}>Error</span>}
          </div>
        </div>

        {/* CLIENT */}
        <div style={{ marginBottom: '20px', position: 'relative' }} ref={clientPickerRef}>
          <label style={LABEL_STYLE}>CLIENT</label>

          {linkedClient ? (
            <div
              style={{
                background: '#2a2a2a',
                border: '1px solid #3a3a3a',
                borderRadius: '6px',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: "'Roboto', sans-serif",
                    fontWeight: 500,
                    fontSize: '15px',
                    color: '#F5F5F5',
                  }}
                >
                  {linkedClient.name}
                </div>
                {(linkedClient.city || linkedClient.state) && (
                  <div
                    style={{
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 400,
                      fontSize: '13px',
                      color: '#888888',
                      marginTop: '2px',
                    }}
                  >
                    {[linkedClient.city, linkedClient.state].filter(Boolean).join(', ')}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={() => setShowClientPicker(true)}
                  style={{
                    background: 'transparent',
                    border: '1px solid #565656',
                    color: '#565656',
                    fontFamily: "'Roboto', sans-serif",
                    fontWeight: 500,
                    fontSize: '12px',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Change
                </button>
                <button
                  onClick={handleRemoveClient}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#BF2634',
                    fontFamily: "'Roboto', sans-serif",
                    fontWeight: 500,
                    fontSize: '12px',
                    cursor: 'pointer',
                    padding: '4px 6px',
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <LinkButton
              label="+ Link Client"
              onClick={() => setShowClientPicker(true)}
            />
          )}

          {showClientPicker && (
            <div style={PICKER_DROPDOWN_STYLE}>
              <input
                autoFocus
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                placeholder="Search clients..."
                style={PICKER_SEARCH_STYLE}
              />
              {filteredClients.length === 0 ? (
                <div
                  style={{
                    padding: '16px',
                    fontFamily: "'Roboto', sans-serif",
                    fontSize: '13px',
                    color: '#565656',
                    fontStyle: 'italic',
                  }}
                >
                  No clients found
                </div>
              ) : (
                filteredClients.map((company) => (
                  <PickerOption
                    key={company._id}
                    primary={company.name}
                    secondary={[company.city, company.state].filter(Boolean).join(', ')}
                    onClick={() => handleSelectClient(company)}
                  />
                ))
              )}
            </div>
          )}
        </div>

        {/* PROJECT MANAGER */}
        <div style={{ marginBottom: '20px', position: 'relative' }} ref={pmPickerRef}>
          <label style={LABEL_STYLE}>PROJECT MANAGER</label>
          {linkedPM ? (
            <div
              style={{
                background: '#2a2a2a',
                border: '1px solid #3a3a3a',
                borderRadius: '6px',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  minWidth: '36px',
                  borderRadius: '50%',
                  backgroundColor: '#565656',
                  border: '2px solid #39FF14',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 700,
                  fontSize: '14px',
                  color: '#F5F5F5',
                }}
              >
                {linkedPM.first_name ? linkedPM.first_name[0].toUpperCase() : '?'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '14px', color: '#F5F5F5' }}>
                  {linkedPM.first_name} {linkedPM.last_name}
                </div>
                {linkedPM.title && (
                  <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 400, fontSize: '13px', color: '#888888' }}>
                    {linkedPM.title}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setShowPMPicker(true)}
                  style={{
                    background: 'transparent',
                    border: '1px solid #565656',
                    color: '#565656',
                    fontFamily: "'Roboto', sans-serif",
                    fontWeight: 500,
                    fontSize: '12px',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Change
                </button>
                <button
                  onClick={handleRemovePM}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#BF2634',
                    fontFamily: "'Roboto', sans-serif",
                    fontWeight: 500,
                    fontSize: '12px',
                    cursor: 'pointer',
                    padding: '4px 6px',
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <LinkButton label="+ Assign Project Manager" onClick={() => setShowPMPicker(true)} />
          )}
          {showPMPicker && (
            <div style={PICKER_DROPDOWN_STYLE}>
              <input
                autoFocus
                value={pmSearch}
                onChange={(e) => setPmSearch(e.target.value)}
                placeholder="Search personnel..."
                style={PICKER_SEARCH_STYLE}
              />
              {filteredPM.length === 0 ? (
                <div style={{ padding: '16px', fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#565656', fontStyle: 'italic' }}>
                  No personnel found
                </div>
              ) : (
                filteredPM.map((profile) => (
                  <PersonnelPickerOption
                    key={profile._id}
                    profile={profile}
                    onClick={() => handleSelectPM(profile)}
                  />
                ))
              )}
            </div>
          )}
        </div>

        {/* TIMELINE */}
        <div style={SECTION_HEADER_STYLE}>TIMELINE</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '20px' }}>
          <div>
            <label style={LABEL_STYLE}>START DATE</label>
            <input
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData((prev) => ({ ...prev, start_date: e.target.value }))}
              onBlur={() => handleBlur('start_date')}
              style={{ ...INPUT_STYLE, colorScheme: 'dark' }}
            />
            {savedFields.start_date && <div style={SAVED_STYLE}>Saved</div>}
            {errorFields.start_date && <div style={ERROR_STYLE}>Error saving</div>}
          </div>
          <div>
            <label style={LABEL_STYLE}>END DATE</label>
            <input
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData((prev) => ({ ...prev, end_date: e.target.value }))}
              onBlur={() => handleBlur('end_date')}
              style={{ ...INPUT_STYLE, colorScheme: 'dark' }}
            />
            {savedFields.end_date && <div style={SAVED_STYLE}>Saved</div>}
            {errorFields.end_date && <div style={ERROR_STYLE}>Error saving</div>}
          </div>
        </div>

        {/* BUDGET */}
        <div style={SECTION_HEADER_STYLE}>BUDGET</div>

        <div style={{ marginBottom: '20px' }}>
          <label style={LABEL_STYLE}>TARGET BUDGET (USD)</label>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid #565656',
            }}
          >
            <span
              style={{
                fontFamily: "'Roboto', sans-serif",
                fontSize: '15px',
                color: '#565656',
                paddingBottom: '4px',
                marginRight: '4px',
                userSelect: 'none',
              }}
            >
              $
            </span>
            <input
              type="text"
              value={budgetDisplay}
              onChange={(e) => setBudgetDisplay(e.target.value.replace(/[^0-9,.]/g, ''))}
              onFocus={() => setBudgetDisplay(String(formData.target_budget || ''))}
              onBlur={() => {
                const parsed = parseCurrency(budgetDisplay);
                setBudgetDisplay(formatCurrency(parsed) || '');
                handleBlur('target_budget', parsed);
              }}
              placeholder="0"
              style={{
                background: 'transparent',
                border: 'none',
                color: '#F5F5F5',
                fontFamily: "'Roboto', sans-serif",
                fontSize: '14px',
                padding: '4px 0',
                outline: 'none',
                flex: 1,
              }}
            />
          </div>
          {savedFields.target_budget && <div style={SAVED_STYLE}>Saved</div>}
          {errorFields.target_budget && <div style={ERROR_STYLE}>Error saving</div>}
          <div
            style={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 300,
              fontSize: '11px',
              color: '#3a3a3a',
              fontStyle: 'italic',
              marginTop: '6px',
            }}
          >
            Currency: USD · Multi-currency coming in Workspace Settings
          </div>
        </div>

        {/* LOCATION */}
        <div style={SECTION_HEADER_STYLE}>LOCATION</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '20px' }}>
          <div>
            <label style={LABEL_STYLE}>CITY</label>
            <input
              value={formData.project_city}
              onChange={(e) => setFormData((prev) => ({ ...prev, project_city: e.target.value }))}
              onBlur={() => handleBlur('project_city')}
              placeholder="City"
              style={INPUT_STYLE}
            />
            {savedFields.project_city && <div style={SAVED_STYLE}>Saved</div>}
          </div>
          <div>
            <label style={LABEL_STYLE}>STATE</label>
            <select
              value={formData.project_state}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, project_state: e.target.value }));
              }}
              onBlur={() => handleBlur('project_state')}
              style={{ ...INPUT_STYLE, cursor: 'pointer', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '4px', padding: '6px 8px' }}
            >
              <option value="">Select state...</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {savedFields.project_state && <div style={SAVED_STYLE}>Saved</div>}
          </div>
        </div>

        {/* Archive */}
        <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #333333' }}>
          <button
            onClick={handleArchive}
            onMouseEnter={() => setArchiveHover(true)}
            onMouseLeave={() => setArchiveHover(false)}
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${archiveHover ? '#E5C04B' : '#565656'}`,
              color: archiveHover ? '#E5C04B' : '#565656',
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 500,
              fontSize: '13px',
              padding: '7px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            Archive Project
          </button>
        </div>
      </div>
    );
  }

  function renderTasksAndNotes() {
    const incomplete = localProject.tasks.filter((t) => !t.is_complete);
    const complete = localProject.tasks.filter((t) => t.is_complete);
    const ordered = [...incomplete, ...complete];

    return (
      <div>
        <div style={{ ...SECTION_HEADER_STYLE, marginTop: '4px' }}>TASKS</div>

        {ordered.length === 0 && (
          <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#565656', marginBottom: '16px' }}>
            No tasks yet
          </div>
        )}

        {ordered.map((task) => {
          const assignee = taskAssignees[task._id] || null;
          const filteredTaskPersonnel = projectTeam.filter((p) =>
            `${p.first_name} ${p.last_name}`.toLowerCase().includes(taskSearch.toLowerCase())
          );
          return (
            <div
              key={task._id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 0',
                borderBottom: '1px solid #2a2a2a',
              }}
            >
              <TaskCheckbox checked={task.is_complete} onChange={() => handleToggleTask(task)} />
              <span
                style={{
                  flex: 1,
                  fontFamily: "'Roboto', sans-serif",
                  fontSize: '14px',
                  color: task.is_complete ? '#565656' : '#F5F5F5',
                  textDecoration: task.is_complete ? 'line-through' : 'none',
                }}
              >
                {task.text}
              </span>
              {/* Assignee button */}
              <div style={{ position: 'relative' }} ref={activeTaskPicker === task._id ? taskPickerRef : null}>
                <div
                  onClick={() => {
                    setActiveTaskPicker(activeTaskPicker === task._id ? null : task._id);
                    setTaskSearch('');
                  }}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                >
                  {assignee ? (
                    <div
                      title={`${assignee.first_name} ${assignee.last_name}`}
                      style={{
                        width: '28px',
                        height: '28px',
                        minWidth: '28px',
                        borderRadius: '50%',
                        backgroundColor: '#565656',
                        border: '2px solid #39FF14',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: "'Roboto', sans-serif",
                        fontWeight: 700,
                        fontSize: '11px',
                        color: '#F5F5F5',
                      }}
                    >
                      {assignee.first_name ? assignee.first_name[0].toUpperCase() : '?'}
                    </div>
                  ) : (
                    <PersonAddOutlined style={{ fontSize: 18, color: '#565656' }} />
                  )}
                </div>
                {activeTaskPicker === task._id && (
                  <div
                    style={{
                      ...PICKER_DROPDOWN_STYLE,
                      right: 0,
                      left: 'auto',
                      width: '220px',
                    }}
                  >
                    <input
                      autoFocus
                      value={taskSearch}
                      onChange={(e) => setTaskSearch(e.target.value)}
                      placeholder="Search team..."
                      style={PICKER_SEARCH_STYLE}
                    />
                    {assignee && (
                      <div
                        onClick={() => handleAssignTask(task._id, null)}
                        style={{
                          padding: '10px 16px',
                          cursor: 'pointer',
                          fontFamily: "'Roboto', sans-serif",
                          fontSize: '13px',
                          color: '#BF2634',
                          borderBottom: '1px solid #3a3a3a',
                        }}
                      >
                        Remove assignee
                      </div>
                    )}
                    {filteredTaskPersonnel.length === 0 ? (
                      <div style={{ padding: '16px', fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#565656', fontStyle: 'italic' }}>
                        No team members
                      </div>
                    ) : (
                      filteredTaskPersonnel.map((profile) => (
                        <PersonnelPickerOption
                          key={profile._id}
                          profile={profile}
                          onClick={() => handleAssignTask(task._id, profile)}
                        />
                      ))
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => handleDeleteTask(task._id)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#565656',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '0 4px',
                  lineHeight: 1,
                  fontFamily: "'Roboto', sans-serif",
                }}
              >
                ×
              </button>
            </div>
          );
        })}

        <div style={{ display: 'flex', gap: '10px', marginTop: '14px', alignItems: 'center' }}>
          <input
            value={newTaskText}
            onChange={(e) => setNewTaskText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddTask(); }}
            placeholder="Add a task..."
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              borderBottom: '1px solid #565656',
              color: '#F5F5F5',
              fontFamily: "'Roboto', sans-serif",
              fontSize: '14px',
              padding: '6px 0',
              outline: 'none',
            }}
          />
          <button
            onClick={handleAddTask}
            disabled={isAddingTask || !newTaskText.trim()}
            style={{
              backgroundColor: 'transparent',
              border: '1px solid #39FF14',
              color: '#39FF14',
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 500,
              fontSize: '12px',
              padding: '5px 14px',
              borderRadius: '4px',
              cursor: isAddingTask || !newTaskText.trim() ? 'default' : 'pointer',
              opacity: isAddingTask || !newTaskText.trim() ? 0.5 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            Add
          </button>
        </div>

        <div style={{ ...SECTION_HEADER_STYLE, marginTop: '40px' }}>NOTES</div>

        <div>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
            onBlur={() => handleBlur('notes')}
            placeholder="Add notes..."
            style={{
              width: '100%',
              backgroundColor: '#1e1e1e',
              border: '1px solid #333333',
              borderRadius: '4px',
              color: '#F5F5F5',
              fontFamily: "'Roboto', sans-serif",
              fontSize: '14px',
              padding: '12px',
              outline: 'none',
              resize: 'vertical',
              minHeight: '160px',
              boxSizing: 'border-box',
              lineHeight: '1.5',
            }}
          />
          {savedFields.notes && <div style={SAVED_STYLE}>Saved</div>}
          {errorFields.notes && <div style={ERROR_STYLE}>Error saving</div>}
        </div>
      </div>
    );
  }

  function renderEvents() {
    return (
      <div>
        {/* Section header row */}
        <div
          style={{
            ...SECTION_HEADER_STYLE,
            marginTop: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>EVENTS</span>
          <button
            onMouseEnter={() => setAddEventHover(true)}
            onMouseLeave={() => setAddEventHover(false)}
            onClick={() => {
              sessionStorage.setItem('vance_new_event_project_id', localProject._id);
              sessionStorage.setItem('vance_new_event_project_name', localProject.title);
              navigate('/events');
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: addEventHover ? '#8B1C26' : '#BF2634',
              color: '#FFFFFF',
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 500,
              fontSize: '13px',
              padding: '8px 16px',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.15s ease',
              marginBottom: '8px',
            }}
          >
            <AddOutlined style={{ fontSize: 16, color: 'inherit' }} />
            Event
          </button>
        </div>

        {isLoadingEvents ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: '48px',
              fontFamily: "'Roboto', sans-serif",
              fontSize: '14px',
              color: '#888888',
            }}
          >
            Loading events...
          </div>
        ) : projectEvents.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: '48px',
              gap: '12px',
            }}
          >
            <EventOutlined style={{ fontSize: 48, color: '#3a3a3a' }} />
            <div
              style={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 300,
                fontSize: '16px',
                color: '#565656',
              }}
            >
              No events yet
            </div>
            <div
              style={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 300,
                fontSize: '13px',
                color: '#3a3a3a',
                textAlign: 'center',
              }}
            >
              Click + Event to create the first event for this project
            </div>
          </div>
        ) : (
          <div>
            {projectEvents.map((event) => (
              <div
                key={event._id}
                onClick={() => {
                  sessionStorage.setItem('vance_select_event', event._id);
                  navigate('/events');
                }}
                onMouseEnter={() => setHoveredEventId(event._id)}
                onMouseLeave={() => setHoveredEventId(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  backgroundColor: '#2a2a2a',
                  border: `1px solid ${hoveredEventId === event._id ? 'rgba(57,255,20,0.3)' : '#3a3a3a'}`,
                  borderRadius: '6px',
                  marginBottom: '8px',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s ease',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 500,
                      fontSize: '15px',
                      color: '#F5F5F5',
                      marginBottom: '2px',
                    }}
                  >
                    {event.title}
                  </div>
                  {(event.location) && (
                    <div
                      style={{
                        fontFamily: "'Roboto', sans-serif",
                        fontWeight: 400,
                        fontSize: '13px',
                        color: '#888888',
                        marginBottom: '2px',
                      }}
                    >
                      {event.location}
                    </div>
                  )}
                  {(event.start_date || event.end_date) && (
                    <div
                      style={{
                        fontFamily: "'Roboto', sans-serif",
                        fontWeight: 400,
                        fontSize: '12px',
                        color: '#565656',
                      }}
                    >
                      {event.start_date ? new Date(event.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                      {event.start_date && event.end_date ? ' — ' : ''}
                      {event.end_date ? new Date(event.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                    </div>
                  )}
                </div>
                <ChevronRightOutlined style={{ fontSize: 20, color: '#565656', flexShrink: 0 }} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderTeam() {
    const teamMemberIds = new Set(
      (localProject.team_members || []).map((m) => m.personnel_profile_id?.toString())
    );
    const filteredPersonnel = availablePersonnel.filter(
      (p) =>
        !teamMemberIds.has(p._id?.toString()) &&
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(memberSearch.toLowerCase())
    );

    return (
      <div>
        {/* Section header row */}
        <div
          style={{
            ...SECTION_HEADER_STYLE,
            marginTop: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>TEAM MEMBERS</span>
          <button
            onClick={() => setShowMemberPicker(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'transparent',
              border: '1px solid #39FF14',
              color: '#39FF14',
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 500,
              fontSize: '12px',
              padding: '4px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
              marginBottom: '8px',
            }}
          >
            <AddOutlined style={{ fontSize: 14 }} />
            Member
          </button>
        </div>

        {/* Member picker */}
        <div style={{ position: 'relative', marginBottom: '16px' }} ref={memberPickerRef}>
          {showMemberPicker && (
            <div style={{ ...PICKER_DROPDOWN_STYLE, maxHeight: '300px' }}>
              <input
                autoFocus
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="Search personnel..."
                style={PICKER_SEARCH_STYLE}
              />
              {filteredPersonnel.length === 0 ? (
                <div
                  style={{
                    padding: '16px',
                    fontFamily: "'Roboto', sans-serif",
                    fontSize: '13px',
                    color: '#565656',
                    fontStyle: 'italic',
                  }}
                >
                  No personnel found
                </div>
              ) : (
                filteredPersonnel.map((profile) => (
                  <PersonnelPickerOption
                    key={profile._id}
                    profile={profile}
                    onClick={() => handleAddMember(profile)}
                  />
                ))
              )}
            </div>
          )}
        </div>

        {/* Team member list */}
        {isLoadingTeam ? (
          <div style={{ color: '#565656', fontFamily: "'Roboto', sans-serif", fontSize: '14px' }}>
            Loading team...
          </div>
        ) : projectTeam.length === 0 ? (
          <div
            style={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 300,
              fontSize: '14px',
              color: '#565656',
            }}
          >
            No team members added yet
          </div>
        ) : (
          projectTeam.map((profile) => {
            const profileId = profile._id?.toString();
            const position = memberPositions[profileId] ?? '';
            return (
              <TeamMemberCard
                key={profileId}
                profile={profile}
                position={position}
                onPositionChange={(val) =>
                  setMemberPositions((prev) => ({ ...prev, [profileId]: val }))
                }
                onPositionBlur={(val) => handleUpdatePosition(profileId, val)}
                onRemove={() => handleRemoveMember(profileId)}
              />
            );
          })
        )}
      </div>
    );
  }

  function renderActivity() {
    const log = [...(localProject.activity_log || [])].reverse();
    if (log.length === 0) {
      return (
        <div style={{ color: '#565656', fontFamily: "'Roboto', sans-serif", fontSize: '14px', marginTop: '24px' }}>
          No activity yet
        </div>
      );
    }
    return (
      <div style={{ marginTop: '4px' }}>
        {log.map((entry, i) => (
          <div
            key={i}
            style={{
              padding: '12px 0',
              borderBottom: '1px solid #2a2a2a',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '16px',
            }}
          >
            <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: '#F5F5F5', flex: 1 }}>
              {entry.detail}
            </span>
            <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '12px', color: '#565656', whiteSpace: 'nowrap' }}>
              {new Date(entry.timestamp).toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div
        style={{
          padding: '20px 32px 0 32px',
          borderBottom: '1px solid #333333',
          backgroundColor: '#222222',
        }}
      >
        <div
          style={{
            fontFamily: "'Roboto', sans-serif",
            fontWeight: 600,
            fontSize: '20px',
            color: '#F5F5F5',
            marginBottom: '16px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {localProject.title}
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', borderBottom: '1px solid #3a3a3a', width: '100%' }}>
          {/* Left group */}
          <div style={{ display: 'flex', flex: 1 }}>
            {TABS.slice(0, -1).map((tab, i) => (
              <div
                key={tab}
                onClick={() => setActiveTab(i)}
                style={{
                  padding: '10px 20px',
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 500,
                  fontSize: '13px',
                  color: activeTab === i ? '#39FF14' : '#888888',
                  borderBottom: activeTab === i ? '2px solid #39FF14' : '2px solid transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                {tab}
              </div>
            ))}
          </div>
          {/* Right group — Activity pinned far right */}
          <div style={{ marginLeft: 'auto' }}>
            {(() => {
              const i = TABS.length - 1;
              const tab = TABS[i];
              return (
                <div
                  key={tab}
                  onClick={() => setActiveTab(i)}
                  style={{
                    padding: '10px 20px',
                    fontFamily: "'Roboto', sans-serif",
                    fontWeight: 500,
                    fontSize: '13px',
                    color: activeTab === i ? '#39FF14' : '#888888',
                    borderBottom: activeTab === i ? '2px solid #39FF14' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '28px 32px',
          backgroundColor: '#222222',
        }}
      >
        {activeTab === 0 && renderOverview()}
        {activeTab === 1 && renderTasksAndNotes()}
        {activeTab === 2 && renderEvents()}
        {activeTab === 3 && renderTeam()}
        {activeTab === 4 && renderActivity()}
      </div>
    </div>
  );
}

// ─── SUB-COMPONENTS ──────────────────────────────────────────────────

function LinkButton({ label, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: hovered ? 'rgba(57,255,20,0.08)' : 'transparent',
        border: '1px solid #39FF14',
        color: '#39FF14',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '13px',
        padding: '8px 16px',
        borderRadius: '4px',
        cursor: 'pointer',
        transition: 'background 0.15s ease',
      }}
    >
      <AddOutlined style={{ fontSize: 16 }} />
      {label}
    </button>
  );
}

function PickerOption({ primary, secondary, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '10px 16px',
        cursor: 'pointer',
        backgroundColor: hovered ? 'rgba(57,255,20,0.06)' : 'transparent',
        transition: 'background 0.1s ease',
      }}
    >
      <div
        style={{
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 500,
          fontSize: '14px',
          color: hovered ? '#39FF14' : '#F5F5F5',
        }}
      >
        {primary}
      </div>
      {secondary && (
        <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '12px', color: '#888888', marginTop: '2px' }}>
          {secondary}
        </div>
      )}
    </div>
  );
}

function PersonnelPickerOption({ profile, onClick }) {
  const [hovered, setHovered] = useState(false);
  const initials = profile.first_name ? profile.first_name[0].toUpperCase() : '?';
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 16px',
        cursor: 'pointer',
        backgroundColor: hovered ? 'rgba(57,255,20,0.06)' : 'transparent',
        transition: 'background 0.1s ease',
      }}
    >
      <div
        style={{
          width: '32px',
          height: '32px',
          minWidth: '32px',
          borderRadius: '50%',
          backgroundColor: '#565656',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 700,
          fontSize: '13px',
          color: '#F5F5F5',
        }}
      >
        {initials}
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontFamily: "'Roboto', sans-serif",
            fontWeight: 500,
            fontSize: '14px',
            color: hovered ? '#39FF14' : '#F5F5F5',
          }}
        >
          {profile.first_name} {profile.last_name}
        </div>
        {profile.cell_phone && (
          <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '12px', color: '#888888' }}>
            {profile.cell_phone}
          </div>
        )}
      </div>
    </div>
  );
}

function TeamMemberCard({ profile, position, onPositionChange, onPositionBlur, onRemove }) {
  const [removeHovered, setRemoveHovered] = useState(false);
  const initials = profile.first_name ? profile.first_name[0].toUpperCase() : '?';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '12px 0',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
      }}
    >
      {/* Avatar */}
      <div
        style={{
          width: '40px',
          height: '40px',
          minWidth: '40px',
          borderRadius: '50%',
          backgroundColor: '#565656',
          border: '2px solid #39FF14',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 700,
          fontSize: '16px',
          color: '#F5F5F5',
        }}
      >
        {initials}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "'Roboto', sans-serif",
            fontWeight: 500,
            fontSize: '15px',
            color: '#F5F5F5',
            marginBottom: '4px',
          }}
        >
          {profile.first_name} {profile.last_name}
        </div>
        <input
          value={position}
          onChange={(e) => onPositionChange(e.target.value)}
          onBlur={(e) => onPositionBlur(e.target.value)}
          placeholder="Add position..."
          style={{
            background: 'transparent',
            border: 'none',
            borderBottom: '1px solid #333333',
            color: '#888888',
            fontFamily: "'Roboto', sans-serif",
            fontSize: '13px',
            padding: '2px 0',
            outline: 'none',
            width: '100%',
            maxWidth: '240px',
          }}
        />
        <div
          style={{
            fontFamily: "'Roboto', sans-serif",
            fontWeight: 300,
            fontSize: '10px',
            color: '#3a3a3a',
            fontStyle: 'italic',
            marginTop: '3px',
          }}
        >
          Position options customizable in Admin Panel
        </div>
        {profile.cell_phone && (
          <div
            style={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 400,
              fontSize: '13px',
              color: '#565656',
              marginTop: '4px',
            }}
          >
            {profile.cell_phone}
          </div>
        )}
      </div>

      {/* Remove */}
      <button
        onClick={onRemove}
        onMouseEnter={() => setRemoveHovered(true)}
        onMouseLeave={() => setRemoveHovered(false)}
        style={{
          background: 'none',
          border: 'none',
          color: removeHovered ? '#BF2634' : '#565656',
          cursor: 'pointer',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          transition: 'color 0.15s ease',
        }}
      >
        <PersonRemoveOutlined style={{ fontSize: 20, color: 'inherit' }} />
      </button>
    </div>
  );
}
